/**
 * Google Auth Page
 *
 * This page is served on the central auth domain (e.g., mylogin.dev.kenbakke.com)
 * and handles Google OAuth authentication for multi-tenant environments.
 *
 * Flow:
 * 1. Tenant subdomain opens this page in a popup with returnOrigin and optional expectedEmail
 * 2. User clicks to authenticate with Google
 * 3. If expectedEmail provided, validates the authenticated user's email matches
 * 4. Sends the result back to the tenant via postMessage
 * 5. Closes the popup
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useGoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import { Button, CircularProgress, Typography, Box, Alert } from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";

import { getGoogleUser } from "../../../api/users/endpoints-referrers";
import { googleDriveConfig } from "../../../common/utilities/constants/constants";
import {
    isValidOrigin,
    getMainDomain,
    sendMessageToOpener,
    GooglePickerMessage
} from "../../../common/utilities/google-picker/google-picker-message.util";

import "./google-auth.scss";

// Reuse the message types from google-picker for consistency
interface GoogleAuthResult {
    success: boolean;
    accessToken?: string;
    email?: string;
    error?: string;
}

type AuthStatus =
    | "initializing"
    | "ready"
    | "authenticating"
    | "validating"
    | "success"
    | "error"
    | "invalid-origin";

interface GoogleAuthContentProps {
    returnOrigin: string;
    expectedEmail: string;
    isOriginValid: boolean;
}

/**
 * Inner component that uses the Google OAuth hook
 * Must be rendered inside GoogleOAuthProvider
 */
const GoogleAuthContent = ({
    returnOrigin,
    expectedEmail,
    isOriginValid
}: GoogleAuthContentProps) => {
    const [status, setStatus] = useState<AuthStatus>("initializing");
    const [errorMessage, setErrorMessage] = useState<string>("");

    // Send result to parent and close
    const sendResultAndClose = useCallback(
        (result: GoogleAuthResult) => {
            const message: GooglePickerMessage = {
                type: "GOOGLE_PICKER_RESULT",
                payload: {
                    success: result.success,
                    accessToken: result.accessToken,
                    url: result.email, // Repurpose url field for email
                    error: result.error
                },
                origin: window.location.origin
            };

            sendMessageToOpener(message, returnOrigin);

            setTimeout(() => {
                window.close();
            }, 100);
        },
        [returnOrigin]
    );

    // Send cancel to parent
    const sendCancelAndClose = useCallback(() => {
        const message: GooglePickerMessage = {
            type: "GOOGLE_PICKER_CANCEL",
            origin: window.location.origin
        };

        sendMessageToOpener(message, returnOrigin);

        setTimeout(() => {
            window.close();
        }, 100);
    }, [returnOrigin]);

    // Initialize and validate on mount
    useEffect(() => {
        if (!returnOrigin) {
            setStatus("invalid-origin");
            setErrorMessage("Missing return origin parameter");
            return;
        }

        if (!isOriginValid) {
            setStatus("invalid-origin");
            setErrorMessage(
                "Invalid origin. This page can only be opened from authorized domains."
            );
            return;
        }

        setStatus("ready");
    }, [returnOrigin, isOriginValid]);

    // Google login handler
    const googleLogin = useGoogleLogin({
        onSuccess: async tokenResponse => {
            setStatus("validating");

            try {
                // Get user info to retrieve email
                const userResponse = await getGoogleUser(tokenResponse.access_token);
                const userEmail = userResponse?.data?.user?.emailAddress;

                // Validate email if expectedEmail was provided
                if (expectedEmail && userEmail !== expectedEmail) {
                    setErrorMessage(`Please sign in with the account: ${expectedEmail}`);
                    setStatus("error");
                    return;
                }

                // Success - send result
                setStatus("success");
                sendResultAndClose({
                    success: true,
                    accessToken: tokenResponse.access_token,
                    email: userEmail
                });
            } catch (error) {
                console.error("GoogleAuthPage: Error getting user info", error);
                setErrorMessage("Failed to verify Google account. Please try again.");
                setStatus("error");
            }
        },
        onError: error => {
            console.error("GoogleAuthPage: Google login error", error);
            setErrorMessage("Google authentication failed. Please try again.");
            setStatus("error");
        }
    });

    // Handle authenticate button click
    const handleAuthenticate = useCallback(() => {
        setStatus("authenticating");
        googleLogin();
    }, [googleLogin]);

    // Handle cancel
    const handleCancel = useCallback(() => {
        sendCancelAndClose();
    }, [sendCancelAndClose]);

    // Handle retry
    const handleRetry = useCallback(() => {
        setErrorMessage("");
        setStatus("ready");
    }, []);

    // Render based on status
    switch (status) {
        case "initializing":
            return (
                <Box className="google-auth-page__loading">
                    <CircularProgress size={40} />
                    <Typography variant="body1">Initializing...</Typography>
                </Box>
            );

        case "invalid-origin":
            return (
                <Box className="google-auth-page__error">
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {errorMessage}
                    </Alert>
                    <Typography variant="body2" color="text.secondary">
                        Please close this window and try again from the application.
                    </Typography>
                </Box>
            );

        case "error":
            return (
                <Box className="google-auth-page__error">
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {errorMessage}
                    </Alert>
                    <Box className="google-auth-page__actions">
                        <Button variant="contained" onClick={handleRetry}>
                            Try Again
                        </Button>
                        <Button variant="text" onClick={handleCancel} sx={{ mt: 1 }}>
                            Cancel
                        </Button>
                    </Box>
                </Box>
            );

        case "ready":
            return (
                <Box className="google-auth-page__ready">
                    <AccountCircleIcon sx={{ fontSize: 64, color: "#4285f4", mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        Google Authentication
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Click the button below to sign in with Google.
                    </Typography>
                    {expectedEmail && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Please use the account: <strong>{expectedEmail}</strong>
                        </Typography>
                    )}
                    <Box className="google-auth-page__actions">
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={handleAuthenticate}
                            startIcon={
                                <img
                                    src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                                    alt="Google"
                                    style={{ width: 20, height: 20 }}
                                />
                            }
                        >
                            Sign in with Google
                        </Button>
                        <Button variant="text" onClick={handleCancel} sx={{ mt: 1 }}>
                            Cancel
                        </Button>
                    </Box>
                </Box>
            );

        case "authenticating":
        case "validating":
            return (
                <Box className="google-auth-page__loading">
                    <CircularProgress size={40} />
                    <Typography variant="body1">
                        {status === "authenticating" ? "Authenticating..." : "Verifying account..."}
                    </Typography>
                </Box>
            );

        case "success":
            return (
                <Box className="google-auth-page__loading">
                    <CircularProgress size={40} />
                    <Typography variant="body1">Authentication complete. Closing...</Typography>
                </Box>
            );

        default:
            return null;
    }
};

/**
 * Main Google Auth Page component
 * Wraps the content with GoogleOAuthProvider
 */
const GoogleAuthPage = () => {
    // Get parameters from URL
    const { returnOrigin, expectedEmail } = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return {
            returnOrigin: params.get("returnOrigin") || "",
            expectedEmail: params.get("expectedEmail") || ""
        };
    }, []);

    // Validate the return origin
    const isOriginValid = useMemo(() => {
        if (!returnOrigin) return false;
        const mainDomain = getMainDomain();
        return isValidOrigin(returnOrigin, [mainDomain]);
    }, [returnOrigin]);

    // Check if Google client ID is configured
    if (!googleDriveConfig.clientId) {
        return (
            <div className="google-auth-page">
                <div className="google-auth-page__container">
                    <Box className="google-auth-page__error">
                        <Alert severity="error" sx={{ mb: 2 }}>
                            Google authentication is not configured.
                        </Alert>
                        <Typography variant="body2" color="text.secondary">
                            Please contact the administrator.
                        </Typography>
                    </Box>
                </div>
            </div>
        );
    }

    return (
        <GoogleOAuthProvider clientId={googleDriveConfig.clientId}>
            <div className="google-auth-page">
                <div className="google-auth-page__container">
                    <GoogleAuthContent
                        returnOrigin={returnOrigin}
                        expectedEmail={expectedEmail}
                        isOriginValid={isOriginValid}
                    />
                </div>
            </div>
        </GoogleOAuthProvider>
    );
};

export default GoogleAuthPage;
